import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

const DISMISS_KEY = "kw_install_dismissed_at";
const DISMISS_DAYS = 14;

/**
 * Shows a small floating "Instalar App" prompt when the browser fires
 * `beforeinstallprompt` (Android Chrome / Edge / Samsung Internet).
 * On iOS Safari no event fires, so we render a one-time hint pointing to
 * the Share → Add to Home Screen flow.
 *
 * User can dismiss; we suppress the banner for 14 days via localStorage.
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);
  const [hidden, setHidden] = useState(() => {
    try {
      const t = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      return t && (Date.now() - t) < DISMISS_DAYS * 86400 * 1000;
    } catch { return false; }
  });

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); window.__pwaPrompt = e; setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); window.__pwaPrompt = null; };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // iOS heuristic — Safari doesn't fire beforeinstallprompt.
  useEffect(() => {
    const ua = navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (isIOS && !isStandalone) setShowIOSHint(true);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* private mode */ }
    setHidden(true);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice.catch(() => {});
    setDeferred(null);
    window.__pwaPrompt = null;
  };

  if (installed || hidden) return null;
  if (!deferred && !showIOSHint) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-[90] kw-card p-4 flex items-center gap-3 kw-fade-up"
      style={{ marginBottom: "env(safe-area-inset-bottom)" }}
      data-testid="install-prompt">
      <Download className="w-5 h-5 text-[#E50000] shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="kw-overline">Instalar app</div>
        {deferred ? (
          <div className="text-xs text-[#A3A3A3]">Adiciona a KGL ao teu ecrã principal para acesso instantâneo.</div>
        ) : (
          <div className="text-xs text-[#A3A3A3]">No iPhone: toca <strong className="text-white">Partilhar</strong> → <strong className="text-white">Adicionar ao Ecrã Principal</strong>.</div>
        )}
      </div>
      {deferred && (
        <button onClick={install} className="kw-btn-primary !py-1.5 !px-3 text-xs" data-testid="install-prompt-btn">
          Instalar
        </button>
      )}
      <button onClick={dismiss} aria-label="Dispensar" className="text-[#737373] hover:text-white shrink-0" data-testid="install-prompt-dismiss">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
